import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios';
import { notifications } from '@mantine/notifications';
import { env } from '../../config/env';
import type { ApiResponse } from '../../types';
import { useAuthStore } from '../../stores/auth';
import { useImpersonationStore } from '../../stores/impersonation';
import { clearAllWizardDrafts } from '../../utils/cohortWizardDraft';
import { signalLogoutToOtherTabs } from '../../hooks/useIdleTimeout';

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

// Flag to prevent multiple concurrent redirects to login
let isRedirecting = false;

// Proactive refresh threshold (refresh if token expires within this many seconds)
const REFRESH_THRESHOLD_SECONDS = 60;

// Decode JWT payload without verification (just to read expiry)
const decodeJwtPayload = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
};

// Check if token is expiring soon (within threshold)
const isTokenExpiringSoon = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true; // If we can't read expiry, assume it needs refresh

  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeUntilExpiry = expiresAt - now;

  return timeUntilExpiry < REFRESH_THRESHOLD_SECONDS * 1000;
};

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper to get auth state from Zustand store (single source of truth)
const getAuthState = () => {
  const state = useAuthStore.getState();
  return {
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    isAuthenticated: state.isAuthenticated,
  };
};

// Helper to update tokens in Zustand store (will persist to localStorage automatically)
const updateTokens = (accessToken: string, refreshToken: string) => {
  useAuthStore.getState().setTokens(accessToken, refreshToken);
};

// Determine the correct login page based on current URL path
const getLoginPath = (): string =>
  window.location.pathname.startsWith('/coach') ? '/coach/login' : '/login';

// Clear auth state and redirect to login
// Note: We clear localStorage directly here because this is called during error handling
// where the store might be in an inconsistent state. The redirect will reload the page
// and Zustand will hydrate from the cleared localStorage.
// The isRedirecting guard prevents multiple concurrent 401s from each triggering
// window.location.href, which overwhelms the browser's History API rate limit.
const clearAuthAndRedirect = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  // MED-1383: the base user of this tab (the admin when impersonating).
  const baseUserId =
    useImpersonationStore.getState().originalUser?.id ?? useAuthStore.getState().user?.id;
  localStorage.removeItem('auth-storage');
  signalLogoutToOtherTabs(baseUserId);
  // MDF-239: these outlive auth-storage, so the next login would inherit them.
  sessionStorage.removeItem('impersonation-session');
  sessionStorage.removeItem('persona-session');
  // MED-1268: wizard drafts belong to the user whose session just ended.
  clearAllWizardDrafts(localStorage);
  window.location.href = getLoginPath();
};

// Proactive token refresh - returns new access token or null if refresh fails
const proactiveRefresh = async (refreshToken: string): Promise<string | null> => {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    try {
      return await refreshPromise;
    } catch {
      return null;
    }
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await axios.post<
        ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }>
      >(`${env.apiUrl}/api/auth/refresh`, { refreshToken });

      const newTokens = response.data.data.tokens;
      updateTokens(newTokens.accessToken, newTokens.refreshToken);
      processQueue(null, newTokens.accessToken);

      return newTokens.accessToken;
    } catch (error) {
      processQueue(new Error('Token refresh failed'));
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Request interceptor with proactive token refresh
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip auth for auth endpoints
    if (
      config.url?.includes('/api/auth/login') ||
      config.url?.includes('/api/auth/refresh')
    ) {
      return config;
    }

    const state = getAuthState();
    if (!state?.accessToken) {
      return config;
    }

    // Check if token is expiring soon and proactively refresh
    if (state.refreshToken && isTokenExpiringSoon(state.accessToken)) {
      try {
        const newAccessToken = await proactiveRefresh(state.refreshToken);
        if (newAccessToken) {
          // MED-1383: a logout names the refresh token to revoke. The refresh
          // above rotated it and revoked the old one, so name the new one, or
          // the new token stays live for 30 days.
          const body = config.data as { refreshToken?: string } | undefined;
          if (
            config.url?.includes('/api/auth/logout') &&
            body?.refreshToken === state.refreshToken
          ) {
            config.data = { ...body, refreshToken: getAuthState().refreshToken };
          }
          config.headers.Authorization = `Bearer ${newAccessToken}`;
          return config;
        }
      } catch {
        // If proactive refresh fails, continue with old token
        // The response interceptor will handle the 401
      }
    }

    // Use current access token
    config.headers.Authorization = `Bearer ${state.accessToken}`;
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor with token refresh logic
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      notifications.show({
        title: 'Too Many Requests',
        message: 'Please wait a moment before trying again.',
        color: 'red',
        autoClose: 5000,
      });
      return Promise.reject(new Error('Too many requests. Please try again later.'));
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints (login, refresh, logout)
      // These 401s are expected for invalid credentials or already-expired sessions
      if (
        originalRequest.url?.includes('/api/auth/login') ||
        originalRequest.url?.includes('/api/auth/refresh') ||
        originalRequest.url?.includes('/api/auth/logout')
      ) {
        // Extract error message and reject normally
        if (error.response?.data) {
          const apiError = error.response.data as { error?: string; message?: string };
          const message = apiError.error || apiError.message || 'Authentication failed';
          return Promise.reject(new Error(message));
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err: Error) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = getAuthState();
      const refreshToken = state?.refreshToken;

      if (!refreshToken) {
        isRefreshing = false;
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      try {
        // Call refresh endpoint
        const response = await axios.post<
          ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }>
        >(`${env.apiUrl}/api/auth/refresh`, { refreshToken });

        const newTokens = response.data.data.tokens;

        // Update tokens in localStorage
        updateTokens(newTokens.accessToken, newTokens.refreshToken);

        // Process queued requests
        processQueue(null, newTokens.accessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(new Error('Token refresh failed'));
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Extract error message from API response and preserve status + structured code + meta
    if (error.response?.data) {
      const apiError = error.response.data as {
        error?: string;
        message?: string;
        code?: string;
        meta?: Record<string, unknown>;
      };
      let message = apiError.error || apiError.message || 'Request failed';
      // A 403 while impersonating means the impersonated user's role lacks
      // access to this screen. The raw "Forbidden" text confuses admins who
      // read it as the client's own error. Replace it with a message that
      // names the impersonation, so the admin knows the real user does not
      // see this and that exiting impersonation restores their own access.
      // MED-1355: rewrite every 403 that has no code. These are the access
      // gates (role and cohort checks), whatever their text. A 403 with a
      // code is a business rule, so keep its text.
      const isAccessGate = !apiError.code;
      if (
        error.response.status === 403 &&
        isAccessGate &&
        useImpersonationStore.getState().isImpersonating
      ) {
        const name = useImpersonationStore.getState().impersonatedUserName || 'a user';
        message = `You are viewing as ${name} (impersonation). This is a Cohort-Manager screen, so it will not load. The real user never sees this error. To edit this, exit impersonation and open this cohort in the Cohort Manager view.`;
      }
      const enhancedError = new Error(message) as Error & {
        status?: number;
        code?: string;
        meta?: Record<string, unknown>;
      };
      enhancedError.status = error.response.status;
      if (apiError.code) enhancedError.code = apiError.code;
      if (apiError.meta) enhancedError.meta = apiError.meta;
      return Promise.reject(enhancedError);
    }

    return Promise.reject(error);
  }
);

// MED-1032 — shape of a single express-validator error returned by the API's
// `validate` middleware in the 422 `meta.errors` array.
interface ApiValidationError {
  field?: string;
  message?: string;
  value?: unknown;
}

/**
 * MED-1032 — Extract the most useful, user-facing message from an error thrown
 * by `apiClient`. The response interceptor attaches `meta` to the thrown Error
 * (see above), so for a 422 validation failure the generic top-level message is
 * "Validation failed" while `meta.errors[].message` holds the field-specific
 * reason (e.g. "Session duration must be between 15 and 480 minutes"). Prefer
 * the field-specific message(s) so the user sees what actually went wrong
 * instead of a generic failure. Falls back to the error message, then `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (error instanceof Error) {
    const meta = (error as Error & { meta?: { errors?: ApiValidationError[] } }).meta;
    const validationErrors = meta?.errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      // De-duplicate: the same message often repeats across array indices when
      // multiple modalities trip the same rule.
      const messages = [...new Set(validationErrors.map(e => e.message).filter(Boolean))];
      if (messages.length > 0) return messages.join('; ');
    }
    return error.message || fallback;
  }
  return fallback;
}

// API client wrapper
export const apiClient = {
  get: async <T>(url: string, params?: object): Promise<T> => {
    const response = await axiosInstance.get<ApiResponse<T>>(url, { params });
    return response.data.data;
  },

  // For paginated endpoints that need both data and pagination info
  getPaginated: async <T>(url: string, params?: object): Promise<{ data: T[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    const response = await axiosInstance.get<ApiResponse<T[]> & { meta?: { pagination?: { page: number; limit: number; total: number; totalPages: number } } }>(url, { params });
    return {
      data: response.data.data,
      pagination: response.data.meta?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    };
  },

  // For endpoints that return data with custom meta fields (like totalActive/totalInactive)
  rawGetWithMeta: async <T>(url: string, params?: object): Promise<{ data: T; meta?: Record<string, unknown> }> => {
    const response = await axiosInstance.get<ApiResponse<T> & { meta?: Record<string, unknown> }>(url, { params });
    return {
      data: response.data.data,
      meta: response.data.meta,
    };
  },

  // `config` is an optional per-request axios override (e.g. a longer `timeout`
  // for slow AI compose endpoints, which legitimately run well past the 30s
  // instance default — see aiApi). It merges over the instance defaults.
  post: async <T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> => {
    const response = await axiosInstance.post<ApiResponse<T>>(url, data, config);
    return response.data.data;
  },

  // For POSTs that need access to the server-side meta object (e.g., MED-832
  // topic create returns meta.created so the UI can choose between "Topic created"
  // and "Topic already exists — selected" toasts).
  rawPostWithMeta: async <T>(
    url: string,
    data?: object,
  ): Promise<{ data: T; meta?: Record<string, unknown> }> => {
    const response = await axiosInstance.post<ApiResponse<T> & { meta?: Record<string, unknown> }>(
      url,
      data,
    );
    return {
      data: response.data.data,
      meta: response.data.meta,
    };
  },

  put: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.put<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  patch: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.patch<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  delete: async <T>(url: string): Promise<T> => {
    const response = await axiosInstance.delete<ApiResponse<T>>(url);
    return response.data.data;
  },

  postFormData: async <T>(url: string, formData: FormData): Promise<T> => {
    const response = await axiosInstance.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  /**
   * GET a URL and return the raw bytes as ArrayBuffer. Used by PDF.js for
   * inline rendering (slide thumbnails + preview modal in MED-784) where
   * the PDF should NOT trigger a download.
   */
  getArrayBuffer: async (url: string): Promise<ArrayBuffer> => {
    const response = await axiosInstance.get(url, { responseType: 'arraybuffer' });
    return response.data as ArrayBuffer;
  },

  /** GET a URL and trigger a browser download. Used for CSV/file exports. */
  downloadFile: async (url: string, defaultFilename: string): Promise<void> => {
    const response = await axiosInstance.get(url, { responseType: 'blob' });
    let filename = defaultFilename;
    const cd = response.headers['content-disposition'];
    if (cd) {
      const match = /filename="?([^"]+)"?/.exec(cd);
      if (match) filename = match[1];
    }
    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },

  /**
   * GET a URL and open the response INLINE in a new browser tab (e.g. a PDF the
   * server sends with `Content-Disposition: inline`). Unlike `downloadFile`,
   * this renders in-tab rather than forcing a download.
   *
   * MED-1104: the only correct way to open an authenticated file endpoint in a
   * new tab. A bare `window.open('/api/...')` (a) resolves against the FRONTEND
   * origin in deployed envs — where the API lives on a separate `env.apiUrl`
   * host, so the SPA renders its "Page Not Found" — and (b) cannot carry the
   * `Authorization: Bearer` header the endpoint requires. Routing through the
   * shared `axiosInstance` fixes both: `baseURL: env.apiUrl` sends it to the
   * right origin, and the request interceptor attaches the bearer token. We
   * fetch the bytes as a blob, wrap them in a same-origin object URL, then open
   * that — so the new tab loads a local blob and never needs its own auth.
   *
   * Note: errors surface via the response interceptor with `.status` attached.
   * Under `responseType: 'blob'` a JSON error body arrives as a Blob, so the
   * thrown message is generic — callers should branch on `.status` for AC5-style
   * messaging. The object URL is revoked after a delay so the opened tab has
   * time to load it.
   */
  openInNewTab: async (url: string): Promise<void> => {
    const response = await axiosInstance.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    // Defer revocation: revoking immediately can race the new tab's load and
    // leave it blank. 60s is comfortably past any reasonable render time.
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  },
};

export default apiClient;
